import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * MediaPlaylistPlayer
 * Carousel custom che alterna video YouTube e TikTok in playlist.
 * - Aspect ratio fisso 9:16 (verticale) o 16:9 (orizzontale)
 * - Autoplay muted (requisito browser)
 * - YouTube: auto-advance quando il video finisce (postMessage API)
 * - TikTok: auto-advance via timer (TikTok embed non espone eventi affidabili)
 * - Loop infinito al termine della playlist
 *
 * Props:
 *   items: array di oggetti { type: 'youtube' | 'tiktok', id: string, durationMs?: number }
 *   aspect: '9/16' (default) | '16/9'
 *   testId: optional data-testid base
 */
const TIKTOK_FALLBACK_DURATION_MS = 28000; // ~28s, durata tipica di uno short

const MediaPlaylistPlayer = ({ items = [], aspect = '9/16', testId = 'media-playlist' }) => {
  const [index, setIndex] = useState(0);
  const iframeRef = useRef(null);
  const tiktokTimerRef = useRef(null);

  const next = useCallback(() => {
    setIndex((i) => (items.length === 0 ? 0 : (i + 1) % items.length));
  }, [items.length]);

  const prev = useCallback(() => {
    setIndex((i) => (items.length === 0 ? 0 : (i - 1 + items.length) % items.length));
  }, [items.length]);

  // Listener postMessage di YouTube IFrame API
  useEffect(() => {
    const onMsg = (event) => {
      if (typeof event.data !== 'string') return;
      try {
        const data = JSON.parse(event.data);
        // YouTube manda { event: "infoDelivery", info: { playerState: 0 } } a fine video
        if (data?.event === 'infoDelivery' && data?.info?.playerState === 0) {
          next();
        }
      } catch {
        /* non JSON: ignoro */
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [next]);

  // Per item TikTok: avanza con timer fallback
  const current = items[index];
  useEffect(() => {
    if (tiktokTimerRef.current) {
      clearTimeout(tiktokTimerRef.current);
      tiktokTimerRef.current = null;
    }
    if (current?.type === 'tiktok') {
      const dur = current.durationMs || TIKTOK_FALLBACK_DURATION_MS;
      tiktokTimerRef.current = setTimeout(() => next(), dur);
    }
    return () => {
      if (tiktokTimerRef.current) clearTimeout(tiktokTimerRef.current);
    };
  }, [index, current, next]);

  if (!current) return null;

  // Costruzione URL embed
  let src = '';
  if (current.type === 'youtube') {
    // enablejsapi=1 abilita postMessage events; mute=1 + autoplay=1; controlli on
    src = `https://www.youtube.com/embed/${current.id}?autoplay=1&mute=1&controls=1&modestbranding=1&rel=0&playsinline=1&enablejsapi=1`;
  } else if (current.type === 'tiktok') {
    src = `https://www.tiktok.com/embed/v2/${current.id}?lang=it-IT&autoplay=1&mute=1`;
  }

  return (
    <div className="relative w-full h-full group" data-testid={`${testId}-container`}>
      <div className="relative w-full h-full overflow-hidden rounded-xl bg-black" style={{ aspectRatio: aspect }}>
        <iframe
          key={`${current.type}-${current.id}-${index}`}
          ref={iframeRef}
          src={src}
          title={`${current.type} ${current.id}`}
          data-testid={`${testId}-iframe`}
          className="absolute inset-0 w-full h-full"
          style={{ border: 0 }}
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      </div>

      {/* Controlli prev/next */}
      <button
        type="button"
        onClick={prev}
        aria-label="Precedente"
        data-testid={`${testId}-prev`}
        className="absolute left-1 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/60 hover:bg-red-600 text-white flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
      >
        <ChevronLeft size={18} />
      </button>
      <button
        type="button"
        onClick={next}
        aria-label="Successivo"
        data-testid={`${testId}-next`}
        className="absolute right-1 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/60 hover:bg-red-600 text-white flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"
      >
        <ChevronRight size={18} />
      </button>

      {/* Indicatore posizione */}
      <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-1 px-2 py-1 rounded-full bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity">
        {items.map((it, i) => (
          <span
            key={`${it.type}-${it.id}-${i}`}
            className={`block w-1.5 h-1.5 rounded-full transition-colors ${
              i === index ? 'bg-red-500' : 'bg-white/40'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default MediaPlaylistPlayer;
